"use client";

import { useState } from "react";
import { googleMapsHref } from "./maps";
import { MapLink } from "./MapLink";
import { ActionSubmit, Banner, FIELD_LABEL, StatusBadge } from "./ui";
import { useFormAction, type FormAction } from "./useFormAction";

/**
 * One waiting business, and the decision on it
 * (docs/ux/screens/AdminRegistrationQueueScreen.md), shared by both frontends
 * (R22 T15).
 *
 * **Approval creates the Business** and rejection deletes the registration
 * (H9), so neither is undoable from this screen and both are the server's to
 * carry out. The reject form asks for a reason before it will submit, because
 * the applicant is told why, and "no" with no reason is the version of this
 * that generates a support conversation instead of ending one.
 *
 * The map link is Google Maps like every other location in the app: an admin
 * checking where a clinic claims to be should land in the map they already use.
 */
export interface RegistrationCommands {
  approve: FormAction;
  reject: FormAction;
}

export interface RegistrationCardData {
  id: string;
  businessName: string;
  website: string | null;
  latitude: number;
  longitude: number;
  categoryNames: string[];
  submittedAt: string;
  applicantName: string;
  applicantEmail: string | null;
}

export function RegistrationCard({
  registration,
  commands,
}: {
  registration: RegistrationCardData;
  commands: RegistrationCommands;
}) {
  const [rejectOpen, setRejectOpen] = useState(false);
  const approve = useFormAction(commands.approve);
  const reject = useFormAction(commands.reject);

  // Google Maps, like every other location link in the app: an admin checking
  // where a clinic claims to be should land in the map they already use.
  const mapHref = googleMapsHref(registration.latitude, registration.longitude);

  return (
    <div className="rounded-2xl border border-line bg-surface p-6 shadow-[var(--shadow-card)]">
      {approve.state.formError && <Banner>{approve.state.formError}</Banner>}
      {reject.state.formError && <Banner>{reject.state.formError}</Banner>}

      <div className="mb-3 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-ink">{registration.businessName}</h2>
          <p className="text-sm text-muted">
            {registration.applicantName}
            {registration.applicantEmail ? ` · ${registration.applicantEmail}` : ""}
          </p>
        </div>
        <span className="shrink-0">
          <StatusBadge tone="warning">Pending</StatusBadge>
        </span>
      </div>

      <dl className="mb-4 space-y-1 text-sm">
        <div className="flex gap-2">
          <dt className="font-medium text-ink">Categories:</dt>
          <dd>{registration.categoryNames.join(", ")}</dd>
        </div>
        <div className="flex gap-2">
          <dt className="font-medium text-ink">Location:</dt>
          <dd>
            <MapLink href={mapHref} />
          </dd>
        </div>
        {registration.website && (
          <div className="flex gap-2">
            <dt className="font-medium text-ink">Website:</dt>
            <dd>
              <a
                href={registration.website}
                target="_blank"
                rel="noreferrer"
                className="text-accent"
              >
                {registration.website}
              </a>
            </dd>
          </div>
        )}
        <div className="flex gap-2">
          <dt className="font-medium text-ink">Submitted:</dt>
          <dd className="font-mono">{new Date(registration.submittedAt).toLocaleString()}</dd>
        </div>
      </dl>

      {!rejectOpen ? (
        <div className="flex gap-3">
          <form onSubmit={approve.onSubmit} className="flex-1">
            <input type="hidden" name="registrationId" value={registration.id} />
            <ActionSubmit pendingLabel="Approving…" pending={approve.pending}>
              Approve
            </ActionSubmit>
          </form>
          <button
            type="button"
            onClick={() => setRejectOpen(true)}
            className="flex-1 rounded-lg border border-danger px-4 py-2.5 font-semibold text-danger hover:bg-danger/5"
          >
            Reject
          </button>
        </div>
      ) : (
        <form
          onSubmit={reject.onSubmit}
          className="rounded-lg border border-line bg-page p-4"
        >
          <input type="hidden" name="registrationId" value={registration.id} />
          <label htmlFor={`note-${registration.id}`} className={FIELD_LABEL}>
            Note to the applicant (optional)
          </label>
          <textarea
            id={`note-${registration.id}`}
            name="note"
            rows={3}
            maxLength={600}
            className="mb-1 w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />
          {reject.state.fieldErrors?.note && (
            <p className="mb-2 text-sm text-danger">{reject.state.fieldErrors.note}</p>
          )}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={reject.pending}
              className="rounded-lg bg-danger px-4 py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-60"
            >
              {reject.pending ? "Rejecting…" : "Confirm rejection"}
            </button>
            <button
              type="button"
              onClick={() => setRejectOpen(false)}
              className="text-sm font-medium text-muted hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
}
